/**
 * Frontend HTML renderer for case study modal body content.
 */
import { getInfoFields } from './modal-constants';
import { escapeHtml } from '../shared/html-entities';

/**
 * Convert plain text / soft line breaks to HTML paragraphs.
 *
 * @param {string} value Raw answer text.
 * @return {string} HTML.
 */
function formatAnswerHtml( value ) {
	const text = String( value || '' ).trim();

	if ( ! text ) {
		return '';
	}

	return text
		.split( /\n{2,}/ )
		.map( ( paragraph ) => {
			const lines = escapeHtml( paragraph ).replace( /\n/g, '<br />' );
			return `<p>${ lines }</p>`;
		} )
		.join( '' );
}

/**
 * Render modal body HTML for a card.
 *
 * @param {Object} cardData Card modal payload.
 * @param {Object} icons    Icon URL map.
 * @return {string} HTML string.
 */
export function renderModalBodyHtml( cardData, icons = {} ) {
	const content = cardData?.modalContent || {};
	const imageUrl = cardData?.imageUrl || '';
	const imageAlt = escapeHtml( cardData?.imageAlt || '' );
	const badgeLabel = cardData?.badgeLabel || '';
	const showBadge = cardData?.badgeEnabled !== false && Boolean( badgeLabel );
	const modalTitle = cardData?.modalTitle || '';
	const showModalTitle =
		cardData?.modalTitleEnabled !== false && Boolean( modalTitle );
	const questions = Array.isArray( content.questions )
		? content.questions
		: [];

	const metaRows = getInfoFields().map( ( field ) => {
		const value = content[ field.key ];

		if ( ! value ) {
			return '';
		}

		const iconUrl = icons[ field.iconKey ] || '';

		return `
			<li class="alteia-tfcs-modal__meta-item">
				<span class="alteia-tfcs-modal__meta-icon" aria-hidden="true">
					<img src="${ escapeHtml(
						iconUrl
					) }" alt="" width="22" height="22" decoding="async" />
				</span>
				<span class="alteia-tfcs-modal__meta-text">
					<strong class="alteia-tfcs-modal__meta-label">${ escapeHtml(
						field.label
					) }:</strong>
					<span class="alteia-tfcs-modal__meta-value">${ escapeHtml( value ) }</span>
				</span>
			</li>
		`;
	} ).join( '' );

	const qaHtml = questions
		.filter( ( item ) => item?.question || item?.answer )
		.map(
			( item ) => `
			<section class="alteia-tfcs-modal__qa-item">
				<h3 class="alteia-tfcs-modal__qa-question">${ escapeHtml(
					item.question || ''
				) }</h3>
				<div class="alteia-tfcs-modal__qa-answer">${ formatAnswerHtml(
					item.answer || ''
				) }</div>
			</section>
		`
		)
		.join( '' );

	return `
		${
			imageUrl
				? `<div class="alteia-tfcs-modal__media">
				<img
					src="${ escapeHtml( imageUrl ) }"
					alt="${ imageAlt }"
					class="alteia-tfcs-modal__image"
					loading="lazy"
					decoding="async"
				/>
			</div>`
				: ''
		}
		<div class="alteia-tfcs-modal__content">
			<div class="alteia-tfcs-modal__layout">
				<aside class="alteia-tfcs-modal__aside">
					${
						showBadge
							? `<span class="alteia-tfcs-modal__badge">${ escapeHtml(
									badgeLabel
							  ) }</span>`
							: ''
					}
					${
						showModalTitle
							? `<p class="alteia-tfcs-modal__title" aria-hidden="true">${ escapeHtml(
									modalTitle
							  ) }</p>`
							: ''
					}
					${ metaRows ? `<ul class="alteia-tfcs-modal__meta">${ metaRows }</ul>` : '' }
				</aside>
				<div class="alteia-tfcs-modal__main">
					${ qaHtml }
				</div>
			</div>
		</div>
	`;
}
